// useSignalSource - the deployed source of a catalog signal, fetched from the
// backend (ListSignalVersions, newest version). The dashboard embeds no copy of
// a detector, so every definition view reads the same source the zygote runs;
// there is nothing to drift.
//
//   null   -> loading (or no signal selected yet)
//   ""     -> resolved with no source (unknown command, empty catalog entry, or a
//            failed fetch - `error` distinguishes the last)
//   string -> the source
// `ready` is the catalog's: until it is true `meta` is null for every command,
// so a caller can tell "still resolving" from "not in this tenant's catalog".
import { useEffect, useMemo, useState } from "react";
import { signalDefByCommand, useSignalCatalog } from "./signalCatalog.js";
import { listSignalVersions } from "./signalsApi.js";

export function useSignalSource(command) {
  const { ready } = useSignalCatalog();
  const meta = useMemo(() => (command && ready ? signalDefByCommand(command) : null), [command, ready]);
  const [source, setSource] = useState(null);
  const [error, setError] = useState(false);
  // Reset SYNCHRONOUSLY when the selected signal changes (render-time), so a
  // switch A->B never paints B's header/chips with A's already-fetched source for
  // a frame - the fetch effect only clears it post-commit.
  const [sourceForId, setSourceForId] = useState(meta?.id ?? null);
  if ((meta?.id ?? null) !== sourceForId) {
    setSourceForId(meta?.id ?? null);
    setSource(null);
    setError(false);
  }
  useEffect(() => {
    if (!meta?.id) return undefined;
    let live = true;
    listSignalVersions(meta.id, {})
      .then((versions) => { if (live) setSource(versions[0]?.source || ""); })
      .catch(() => { if (live) { setSource(""); setError(true); } });
    return () => { live = false; };
  }, [meta?.id]);
  return { ready, meta, source, error };
}

// The trigger-shaped definition SignalSourceEditor renders and the sample tester
// evaluates - one python step carrying the backend source. Null until loaded.
// `identity` lets a caller keep its own command/title/description (the built-in
// entry's copy) over the catalog's.
export function signalSourceDefinition(meta, source, identity = {}) {
  if (!meta || source == null) return null;
  return {
    command: identity.command ?? meta.command,
    title: identity.title ?? meta.label,
    description: identity.description ?? meta.description,
    steps: [{ kind: "python", id: "signal", entry: "run", code: source }],
  };
}
